import React from "react";
import { Box, Dropdown, Menu, MenuButton, MenuItem } from "@mui/joy";
import { FlagOutlinedIcon, SUndo } from "../../../styles/StyledMUI";
import {
  colorTokens,
  listRowAccentPlainSx,
  trainingStatusGlyphSizes,
  trainingStatusMenuItemSx,
  trainingStatusMenuSx,
} from "../../../styles/globalStyles";
import ActionTooltip from "../../common/ActionTooltip";
import IconButtonCTA from "../../common/IconButtonCTA";
import { trainingCardActionSlotSx } from "./trainingCardActionsSx";
import {
  trainingStatusActionA11yFr,
  trainingStatusActionEmoji,
} from "./trainingStatusActionTokens";
import type { PendingStatusAction, TrainingStatusAction } from "./trainingStatusActionTokens";

export type { PendingStatusAction, TrainingStatusAction };

type TrainingStatusActionSelectProps = {
  listKey: string | number;
  allowedActions: readonly TrainingStatusAction[];
  disabled?: boolean;
  onAction: (action: TrainingStatusAction) => void | Promise<void>;
  inlineCompact?: boolean;
  pendingAction?: PendingStatusAction | null;
  onClearPending?: () => void;
};

type OptionDef = { value: TrainingStatusAction; emoji: string; label: string };

const inlineRootSx = {
  display: "inline-flex" as const,
  alignItems: "center" as const,
  justifyContent: "center" as const,
  width: 32,
  minWidth: 32,
  height: 32,
  flexShrink: 0,
};

export default function TrainingStatusActionSelect({
  listKey,
  allowedActions,
  disabled = false,
  onAction,
  inlineCompact = false,
  pendingAction = null,
  onClearPending,
}: TrainingStatusActionSelectProps) {
  const options = React.useMemo((): OptionDef[] => {
    return allowedActions.map((v) => ({
      value: v,
      emoji: trainingStatusActionEmoji[v],
      label: trainingStatusActionA11yFr[v],
    }));
  }, [allowedActions]);

  if (pendingAction && onClearPending) {
    return (
      <ActionTooltip
        title="Action en attente — validation du décompte · cliquer pour retirer"
        disabled={disabled}
      >
        <Box
          key={String(listKey)}
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
            boxShadow: `0 0 0 2px ${colorTokens.accent.primary}66`,
            borderRadius: "8px",
            minHeight: inlineCompact ? 32 : 36,
            height: inlineCompact ? 32 : 36,
            boxSizing: "border-box",
          }}
        >
          <IconButtonCTA
            size="sm"
            variant="plain"
            ctaVariant="accent"
            aria-label="Retirer"
            disabled={disabled}
            onClick={() => onClearPending()}
            sx={{
              p: 0,
              minWidth: inlineCompact ? 28 : 32,
              width: inlineCompact ? 28 : 32,
              height: inlineCompact ? 28 : 32,
              border: "none",
              ...listRowAccentPlainSx,
            }}
          >
            <SUndo
              aria-hidden
              sx={{
                fontSize: trainingStatusGlyphSizes.pendingRevertIcon,
                lineHeight: 1,
                display: "inline-flex",
              }}
            />
          </IconButtonCTA>
        </Box>
      </ActionTooltip>
    );
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <Box key={String(listKey)} sx={inlineCompact ? inlineRootSx : trainingCardActionSlotSx}>
      <Dropdown>
        <ActionTooltip title="Changer le statut" disabled={disabled}>
          <Box component="span" sx={{ display: "inline-flex", lineHeight: 0 }}>
            <MenuButton
              disabled={disabled}
              slots={{ root: IconButtonCTA }}
              slotProps={{
                root: {
                  size: "sm",
                  variant: "plain",
                  ctaVariant: "accent",
                  "aria-label": "Changer le statut de la formation",
                  sx: { ...listRowAccentPlainSx, border: "none" },
                  disabled,
                },
              }}
              sx={{
                p: 0,
                minWidth: 0,
                width: inlineCompact ? 32 : 36,
                height: inlineCompact ? 32 : 36,
              }}
            >
              <FlagOutlinedIcon
                sx={{
                  color: colorTokens.accent.primary,
                  fontSize: inlineCompact ? "1.15rem" : undefined,
                }}
              />
            </MenuButton>
          </Box>
        </ActionTooltip>
        <Menu
          placement="bottom-end"
          size="sm"
          variant="outlined"
          color="neutral"
          sx={trainingStatusMenuSx}
        >
          {options.map((o) => {
            const isDanger = o.value === "annuler";
            return (
              <MenuItem
                key={o.value}
                variant="plain"
                color="neutral"
                onClick={async (e) => {
                  e.preventDefault();
                  await onAction(o.value);
                }}
                sx={trainingStatusMenuItemSx(isDanger)}
              >
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    fontSize: trainingStatusGlyphSizes.menuItem,
                    lineHeight: 1,
                    display: "inline-flex",
                    flexShrink: 0,
                    opacity: 0.95,
                  }}
                >
                  {o.emoji}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.875rem",
                    lineHeight: 1.25,
                    textAlign: "left",
                    whiteSpace: "normal",
                    flex: 1,
                  }}
                >
                  {o.label}
                </Box>
              </MenuItem>
            );
          })}
        </Menu>
      </Dropdown>
    </Box>
  );
}
